// Show cover art: preview, requirements, and upload with a pre-flight
// check so a rejected image never reaches Apple.

import { useRef, useState } from 'react';
import { checkArtworkSize, imageSize, useUploadArtwork, type Show } from '../../lib/shows';
import { errorInfo } from '../../lib/api';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';

interface Props {
  show: Show;
  /** Injectable for tests (jsdom can't decode images). */
  readSize?: (file: File) => Promise<{ width: number; height: number }>;
}

export function CoverArt({ show, readSize = imageSize }: Props) {
  const upload = useUploadArtwork(show.id);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  const onFile = async (file: File) => {
    setProblem(null);
    setChecking(true);
    try {
      const { width, height } = await readSize(file);
      const check = checkArtworkSize(width, height, file.type);
      if (!check.ok) {
        setProblem(check.message);
        return;
      }
      upload.mutate(file, {
        onSuccess: () => toast.show('Cover art updated', 'success'),
        onError: (err) => setProblem(errorInfo(err).message),
      });
    } catch {
      setProblem('That file could not be read as an image.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <section aria-labelledby="cover-heading" className="flex flex-wrap items-start gap-5 rounded-xl border border-border p-5">
      {show.artworkUrl ? (
        <img src={show.artworkUrl} alt={`${show.title} cover art`} className="size-32 rounded-lg object-cover" />
      ) : (
        <div className="flex size-32 items-center justify-center rounded-lg border-2 border-dashed border-border text-xs text-muted">
          No cover art
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-2">
        <h2 id="cover-heading" className="text-lg font-semibold">
          Cover art
        </h2>
        <p className="text-sm text-muted">Square JPEG or PNG, 1400–3000 px. Apple Podcasts and Spotify require it.</p>
        {problem && (
          <p role="alert" className="text-sm text-danger">
            {problem}
          </p>
        )}
        <Button variant="secondary" loading={checking || upload.isPending} onClick={() => input.current?.click()}>
          {show.artworkUrl ? 'Replace cover art' : 'Upload cover art'}
        </Button>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          data-testid="cover-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
            e.target.value = '';
          }}
        />
      </div>
    </section>
  );
}
